import type { ManualSection } from '../manual.types';

export const introduccionSection: ManualSection = {
  slug: 'introduccion',
  title: 'Introducción',
  icon: 'ki-book-open',
  summary: 'Qué es Raptor, cómo viaja una llamada y qué hace cada pantalla.',
  audience: 'Todos. Empieza por aquí.',
  topics: [
    {
      id: 'que-es-raptor',
      title: 'Qué es Raptor',
      blocks: [
        {
          kind: 'p',
          text: 'Raptor es el portal de administración del **CPD** de Voices Tec: la detección de buzón de voz en las llamadas salientes masivas de la plataforma Blaster. Desde aquí se configura qué buscar en cada carrier, se administra el ruteo de OpenSIPS y se consulta qué está pasando con el tráfico.'
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Raptor no procesa audio',
          text: 'La detección la hacen los FreeSWITCH. Raptor guarda la configuración, se la entrega a los nodos, recoge la telemetría y la presenta. Entender esto explica casi todo el comportamiento del portal — por ejemplo, por qué guardar un cambio y aplicarlo son dos pasos distintos.'
        }
      ]
    },
    {
      id: 'flujo-de-una-llamada',
      title: 'El camino de una llamada',
      blocks: [
        {
          kind: 'code',
          text: 'Blaster  →  FreeSWITCH  →  OpenSIPS  →  Carrier  →  Teléfono\n              │\n              └─ durante el early media corre el CPD\n                 y cuelga con 603 si detecta buzón'
        },
        {
          kind: 'steps',
          items: [
            'El **Blaster** decide a quién llamar y le pide a un **FreeSWITCH** que origine la llamada. En esa petición manda el operador y el grupo de ruta.',
            'El **FS** pide a Raptor la política de CPD que le toca a esa combinación y la deja en caché.',
            'La llamada sale por **OpenSIPS** hacia el carrier y de ahí a la red telefónica.',
            'Mientras suena (**early media**), el FS aplica la detección. Si concluye que es un buzón, cuelga con **SIP 603** antes de que el carrier facture.',
            'Cada paso emite un evento que llega a Raptor y termina alimentando el **Dashboard** y los **Reportes**.'
          ]
        },
        {
          kind: 'p',
          text: 'El detalle de cómo llega un evento al dashboard está en [Dashboard → De dónde salen los datos](/manual/dashboard#de-donde-salen-los-datos).'
        }
      ]
    },
    {
      id: 'mapa-de-pantallas',
      title: 'Mapa de pantallas',
      blocks: [
        {
          kind: 'table',
          head: ['Pantalla', 'Para qué', 'Rol mínimo'],
          rows: [
            [
              '[Dashboard](/manual/dashboard)',
              'Qué está pasando ahora y qué pasó en un rango.',
              'operador'
            ],
            [
              '[Reportes](/manual/reportes)',
              'Consulta a detalle, con filtros y exportación.',
              'operador'
            ],
            [
              '[CPD → Templates](/manual/cpd)',
              'Políticas completas reutilizables.',
              'supervisor para escribir'
            ],
            ['[CPD → Operators](/manual/cpd)', 'Ajustes por operador.', 'supervisor para escribir'],
            [
              '[CPD → Routes](/manual/cpd)',
              'Ajustes por grupo de ruta / carrier.',
              'supervisor para escribir'
            ],
            [
              '[CPD → Overrides](/manual/cpd)',
              'Ajustes por operador + ruta. La capa que gana.',
              'supervisor para escribir'
            ],
            [
              '[Entrantes y Salientes](/manual/opensips)',
              'Ruteo de OpenSIPS.',
              'supervisor / admin'
            ],
            ['[Dialplan](/manual/opensips)', 'Reglas de transformación del número.', 'supervisor'],
            ['[Usuarios](/manual/usuarios)', 'Alta y administración de cuentas.', 'admin'],
            ['[Auditoría](/manual/auditoria)', 'Quién cambió qué y cuándo.', 'admin']
          ]
        }
      ]
    },
    {
      id: 'como-usar-este-manual',
      title: 'Cómo usar este manual',
      blocks: [
        {
          kind: 'ul',
          items: [
            'El **buscador** de la portada busca en el texto de todas las secciones y te lleva directo al tema.',
            'Cada tema tiene su propio enlace: al pasar el cursor sobre un título aparece el símbolo `#` para copiar la liga y pegarla en un ticket.',
            'Los recuadros **rojos** son cosas que se prestan a reportarse como bug sin serlo, o que sí requieren cuidado. Vale la pena leerlos completos.',
            'Si vas a validar un número, empieza siempre por [las definiciones de Reportes](/manual/reportes#definiciones).'
          ]
        }
      ]
    }
  ]
};
